import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography
} from '@mui/material';
import Container from '@mui/material/Container';
import { Close, Save } from '@mui/icons-material';
import { Auth } from 'aws-amplify';
import {
  getBrazilSectionSeparation,
  resolveBrazilSectionSeparationDetail,
  saveBrazilSectionSeparation
} from '../../api/brazil';
import { resolveSectionSeparationSaveResponse } from '../../api/sectionSeparation';
import LabelSeparationDialogHeader from '../Common/LabelSeparation/LabelSeparationDialogHeader';
import { SectionSeparationDetail } from '../Common/SectionSeparation/types';
import Actions from '../../store/actions';
import Store from '../../store';
import styles from '../Common/SectionSeparation/sectionSeparation.styles';

const POPUP_OPTIONS =
  'height=1000,width=1000,left=100,top=100,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,directories=no, status=yes';

type BrazilSectionSeparationDialogProps = {
  documentId: number;
  registrationNumber?: string;
  onClose: () => void;
  onSaved: () => void;
  /* eslint-disable no-unused-vars -- type-only callback signature */
  onBusyChange?: (busy: boolean) => void;
  /* eslint-enable no-unused-vars */
};

const BrazilSectionSeparationDialog = ({
  documentId,
  registrationNumber = '',
  onClose,
  onSaved,
  onBusyChange
}: BrazilSectionSeparationDialogProps) => {
  const { dispatch } = useContext<any>(Store);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState<SectionSeparationDetail | null>(null);
  const [openSubmitConfirmation, setOpenSubmitConfirmation] = useState(false);
  const isMountedRef = useRef(true);
  const popupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const busy = loading || submitting;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (popupTimerRef.current) {
        clearInterval(popupTimerRef.current);
      }
      window.onbeforeunload = null;
      onBusyChange?.(false);
    };
  }, [onBusyChange]);

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const handleClose = useCallback(() => {
    if (busy) {
      return;
    }
    onClose();
  }, [busy, onClose]);

  const watchPopupClose = useCallback((newWindow: Window | null) => {
    if (popupTimerRef.current) {
      clearInterval(popupTimerRef.current);
    }
    popupTimerRef.current = setInterval(() => {
      if (newWindow?.closed) {
        if (popupTimerRef.current) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;
        }
        setOpenSubmitConfirmation(true);
      }
    }, 1000);
  }, []);

  const handleOpenDocEdit = useCallback(() => {
    if (!details?.onedrive_file_url) {
      return;
    }
    const newWindow = window.open(details.onedrive_file_url, '_blank', POPUP_OPTIONS);
    watchPopupClose(newWindow);
  }, [details?.onedrive_file_url, watchPopupClose]);

  const handleOpenOriginalPdf = useCallback(() => {
    const url = details?.document_link ?? '';
    if (!url) {
      return;
    }
    const newWindow = window.open(url, '_blank', POPUP_OPTIONS);
    watchPopupClose(newWindow);
  }, [details?.document_link, watchPopupClose]);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        window.onbeforeunload = () => 'Please wait for the data to load...';
        setLoading(true);
        const response = await getBrazilSectionSeparation(documentId);
        const result = resolveBrazilSectionSeparationDetail(response);

        if (!result.ok) {
          await dispatch({
            type: Actions.SET_ALERT,
            value: { status: true, message: result.message }
          });
          onClose();
          return;
        }

        if (!isMountedRef.current) {
          return;
        }

        setDetails(result.data);
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Failed to load section separation';
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message }
        });
        onClose();
      } finally {
        window.onbeforeunload = null;
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchDetail();
  }, [documentId, dispatch, onClose]);

  useEffect(() => {
    if (loading || !details) {
      return;
    }

    if (details.onedrive_file_url) {
      handleOpenDocEdit();
      return;
    }

    dispatch({
      type: Actions.SET_ALERT,
      value: { status: true, message: 'No document available' }
    });
  }, [details, dispatch, handleOpenDocEdit, loading]);

  const buildSubmitPayload = useCallback(async () => {
    if (!details?.onedrive_doc_name || !details.onedrive_doc_id || !details.s3_path) {
      return null;
    }

    const payload = {
      filename: details.onedrive_doc_name,
      s3_path: details.s3_path,
      fileId: details.onedrive_doc_id
    };

    try {
      const user = await Auth.currentUserInfo();
      return {
        ...payload,
        curator_name: user.attributes['custom:user'],
        curator_email: user.attributes.email
      };
    } catch {
      return payload;
    }
  }, [details]);

  const handleSubmitChanges = useCallback(async () => {
    const payload = await buildSubmitPayload();
    if (!payload) {
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Missing document metadata for submission' }
      });
      return;
    }

    setOpenSubmitConfirmation(false);
    setSubmitting(true);
    try {
      window.onbeforeunload = () => 'Please wait for the submission to complete...';
      const response = await saveBrazilSectionSeparation(documentId, payload);
      const result = resolveSectionSeparationSaveResponse(response);

      if (!result.ok) {
        await dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: result.message }
        });
        return;
      }

      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: result.message, color: 'success' }
      });
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to submit section separation';
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    } finally {
      window.onbeforeunload = null;
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [buildSubmitPayload, dispatch, documentId, onClose, onSaved]);

  return (
    <>
      <Container maxWidth={false}>
        <LabelSeparationDialogHeader
          title='Section Separation'
          subtitle={registrationNumber}
          closeDisabled={busy}
          onClose={handleClose}
        />

        <Box sx={styles.actionBar}>
          <Button
            variant='contained'
            disabled={busy || !details?.document_link}
            sx={styles.greenButton}
            onClick={handleOpenOriginalPdf}>
            View Original PDF
          </Button>
        </Box>

        {loading || submitting ? (
          <Box sx={styles.loadingBox}>
            <LinearProgress sx={styles.progressBar} color='secondary' />
            <Typography sx={styles.loadingText}>
              {loading ? 'Loading data...' : 'Submitting changes...'}
            </Typography>
            {loading && (
              <Typography sx={styles.loadingSubText}>
                (this might take a couple of minutes to complete)
              </Typography>
            )}
          </Box>
        ) : (
          details?.onedrive_file_url && (
            <Box sx={styles.fileOpenBox}>
              <Typography sx={styles.fileOpenText}>File is open in a new popup window</Typography>
              <Button
                variant='contained'
                disabled={busy}
                sx={styles.greenButton}
                onClick={handleOpenDocEdit}>
                Open file again
              </Button>
            </Box>
          )
        )}
      </Container>

      <Dialog
        fullWidth
        maxWidth='xs'
        open={openSubmitConfirmation}
        disableEscapeKeyDown={submitting}
        onClose={() => {
          if (submitting) {
            return;
          }
          setOpenSubmitConfirmation(false);
        }}>
        <DialogTitle id='section-separation-confirm-title'>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography sx={styles.confirmTitle}>Confirm submission</Typography>
            <IconButton
              aria-label='close'
              disabled={submitting}
              onClick={() => {
                if (submitting) {
                  return;
                }
                setOpenSubmitConfirmation(false);
              }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={styles.confirmBody}>
            Are you sure you have edited the document accurately?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={handleOpenDocEdit} sx={styles.greenButtonWithMargin}>
            Open file again
          </Button>
          <Button
            startIcon={<Save />}
            variant='contained'
            disabled={submitting}
            onClick={handleSubmitChanges}
            sx={styles.greenButtonWithMargin}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

BrazilSectionSeparationDialog.defaultProps = {
  registrationNumber: '',
  onBusyChange: undefined
};

export default BrazilSectionSeparationDialog;
